/**
 * Deterministic R2 object layout and Firestore document paths, shared by the
 * ingest pipeline (writer), Cloud Functions (signer), Worker (server), and
 * app (reader).
 *
 * R2 layout per dance — everything playback needs lives under one prefix so
 * a single path-scoped token covers it:
 *
 *   studios/{studioId}/recitals/{recitalId}/performances/{performanceId}/dances/{danceId}/
 *     master.m3u8
 *     1080p.m3u8   1080p-init.mp4   1080p-00001.m4s ...
 *     720p.m3u8    720p-init.mp4    720p-00001.m4s ...
 *     audio-audience.m3u8  audio-audience-init.mp4  audio-audience-00001.m4s ...
 *     audio-clean.m3u8     audio-clean-init.mp4     audio-clean-00001.m4s ... (optional)
 *     thumb.jpg
 *     storyboard.jpg
 *     storyboard.vtt
 */
export function recitalMediaPrefix(studioId, recitalId) {
    return `studios/${studioId}/recitals/${recitalId}/`;
}
export function dancePrefix(studioId, recitalId, performanceId, danceId) {
    return `${recitalMediaPrefix(studioId, recitalId)}performances/${performanceId}/dances/${danceId}/`;
}
export function danceMasterPlaylistKey(studioId, recitalId, performanceId, danceId) {
    return `${dancePrefix(studioId, recitalId, performanceId, danceId)}master.m3u8`;
}
export function danceH264MasterPlaylistKey(studioId, recitalId, performanceId, danceId) {
    return `${dancePrefix(studioId, recitalId, performanceId, danceId)}master-h264.m3u8`;
}
export function danceHevcMasterPlaylistKey(studioId, recitalId, performanceId, danceId) {
    return `${dancePrefix(studioId, recitalId, performanceId, danceId)}master-hevc.m3u8`;
}
export function danceAudioPlaylistKey(studioId, recitalId, performanceId, danceId, trackId) {
    return `${dancePrefix(studioId, recitalId, performanceId, danceId)}audio-${trackId}.m3u8`;
}
export function danceThumbKey(studioId, recitalId, performanceId, danceId) {
    return `${dancePrefix(studioId, recitalId, performanceId, danceId)}thumb.jpg`;
}
export function danceStoryboardVttKey(studioId, recitalId, performanceId, danceId) {
    return `${dancePrefix(studioId, recitalId, performanceId, danceId)}storyboard.vtt`;
}
/** Derives the dance's R2 prefix from its master playlist key. */
export function prefixFromHlsPath(hlsPath) {
    return hlsPath.replace(/[^/]*$/, "");
}
// ── Firestore document paths ──
export const firestorePaths = {
    studio: (studioId) => `studios/${studioId}`,
    recital: (recitalId) => `recitals/${recitalId}`,
    performance: (recitalId, performanceId) => `recitals/${recitalId}/performances/${performanceId}`,
    dance: (recitalId, performanceId, danceId) => `recitals/${recitalId}/performances/${performanceId}/dances/${danceId}`,
    dancerIndex: (recitalId, performanceId) => `recitals/${recitalId}/performances/${performanceId}/meta/dancerIndex`,
    familyAccount: (uid) => `familyAccounts/${uid}`,
    grant: (uid, recitalId) => `familyAccounts/${uid}/grants/${recitalId}`,
    device: (uid, deviceId) => `familyAccounts/${uid}/devices/${deviceId}`,
    savedDancer: (uid, dancerId) => `familyAccounts/${uid}/dancers/${dancerId}`,
    resume: (uid, performanceId) => `familyAccounts/${uid}/resume/${performanceId}`,
    recitalResume: (uid, recitalId) => `familyAccounts/${uid}/recitalResume/${recitalId}`,
    session: (uid, sessionId) => `familyAccounts/${uid}/sessions/${sessionId}`,
    playbackChunk: (playId, sequence) => `playbackChunks/${playId}_${sequence}`,
    pendingGrant: (pendingGrantId) => `pendingGrants/${pendingGrantId}`,
    giftCode: (giftCodeId) => `giftCodes/${giftCodeId}`,
    auditLog: (entryId) => `adminAuditLog/${entryId}`
};
